[
  {
    "id": "w01",
    "t": "Set up the project",
    "why": "Tempo, key and style decide almost every number that follows: delay times, bass frequencies, loudness targets. Write them down before you start.",
    "todo": [
      "Create the session at 44.1 or 48 kHz, 24-bit",
      "Set the tempo and time signature",
      "Name tracks and color-code groups (drums, bass, sample, vocals, FX)",
      "Set up group buses: drums, bass, music, vocals"
    ],
    "setup": true,
    "links": null
  },
  {
    "id": "w02",
    "t": "Dig for the sample",
    "why": "The sample is the song. A great loop with average drums beats great drums over a weak loop.",
    "todo": [
      "Collect 3–5 candidate records or loops before committing",
      "Mark the cleanest section: intro, breakdown, solo",
      "Note the sample's key and original tempo",
      "Check the bass is usable for Low End Theory, or plan to replay it"
    ],
    "setup": false,
    "links": [
      [
        "crate",
        "Crate tab"
      ]
    ]
  },
  {
    "id": "w03",
    "t": "Chop and flip",
    "why": "Repitching (old-sampler style) changes speed and pitch together and sounds warm; time-stretching keeps pitch but can smear. Fades stop clicks at every chop.",
    "todo": [
      "Decide: repitch to the tempo, or time-stretch",
      "Chop at transients; fade every slice 1–5 ms",
      "Find the loop point where the groove feels best",
      "Pitch or re-sequence chops into the new riff",
      "Make an A and B variation for hooks"
    ],
    "setup": false,
    "links": null
  },
  {
    "id": "w04",
    "t": "Program the drums",
    "why": "Drums set the pocket. Pick sounds that fit the sample's era, then set swing before adding notes, not after.",
    "todo": [
      "Choose kick, snare and hats that match the sample's grit",
      "Tune the kick to the root or fifth",
      "Set swing and program the main groove",
      "Layer the snare (break + one-shot or rim) if it lacks punch",
      "Add ghost notes and percussion for movement"
    ],
    "setup": false,
    "links": null
  },
  {
    "id": "w05",
    "t": "Build the low end",
    "why": "Only one thing should own the sub at any moment. Low End Theory gives you a bass track that is already in key and in the pocket.",
    "todo": [
      "Duplicate the sample, low-pass the copy",
      "High-pass the original so the split is clean",
      "Compress the bass track for even notes",
      "Mono the bass and add light saturation",
      "Decide who owns the sub: kick or bass",
      "Sidechain or rearrange where they collide"
    ],
    "setup": false,
    "links": [
      [
        "low",
        "Low End builder"
      ]
    ]
  },
  {
    "id": "w06",
    "t": "Arrange",
    "why": "Classic beats loop, but something changes every 4 or 8 bars. Subtraction is the strongest arrangement move.",
    "todo": [
      "Intro 4–8 bars (filtered sample or bass-only)",
      "Verse 16 bars, hook 8 bars, verse, hook",
      "Mark changes every 4/8 bars: drop kick, filter the loop, add a stab",
      "Leave space for the vocal; drop elements under key lines",
      "Plan scratch or vocal-chop hooks if the style calls for them"
    ],
    "setup": false,
    "links": null
  },
  {
    "id": "w07",
    "t": "Gain staging",
    "why": "Digital clips hard at 0 dBFS, and analog-modeled plugins are built to sound best around −18 dBFS average (0 VU). Good staging keeps plugins in their sweet spot and leaves the master headroom. It is not about loudness: loudness comes at mastering.",
    "todo": [
      "Set all faders to 0 dB; set levels with clip gain or a trim plugin instead",
      "Trim hot vinyl rips and normalized samples down",
      "Kick (or kick + 808) peaks around −10 to −12 dBFS on its channel",
      "Loop and other tracks average near −18 dBFS",
      "Plugins: bypassing one shouldn't jump the level (match in/out)",
      "Master bus peaks −6 to −3 dBFS with nothing on it",
      "No red anywhere, including inside plugins"
    ],
    "setup": false,
    "links": null
  },
  {
    "id": "w08",
    "t": "Balance and pan",
    "why": "A good static mix with only faders and pans gets you 80% of the way. EQ and compression then refine, not rescue.",
    "todo": [
      "Start with kick and bass, then snare, then sample, then vocals",
      "Mix at moderate volume (75–85 dB SPL)",
      "Center: kick, snare, bass, lead vocal",
      "Hats and percs slightly off-center; doubles and pads wide",
      "Check in mono"
    ],
    "setup": false,
    "links": [
      [
        "fx",
        "Stereo rules"
      ]
    ]
  },
  {
    "id": "w09",
    "t": "EQ",
    "why": "Subtract first, boost second. Most mud is buildup across many tracks in 150–400 Hz, not one bad track.",
    "todo": [
      "High-pass everything that isn't bass",
      "Clean 200–400 Hz on the sample and pads",
      "Carve a 2–4 kHz pocket in the beat for the vocal",
      "Boost presence and air only after cuts",
      "A/B every move at matched level"
    ],
    "setup": false,
    "links": [
      [
        "eq",
        "EQ by source"
      ],
      [
        "spec",
        "Spectrum"
      ]
    ]
  },
  {
    "id": "w10",
    "t": "Compression",
    "why": "Compression shapes transients and evens out level. Slow attack means more punch, fast attack means more control; release should recover before the next hit.",
    "todo": [
      "Drum bus glue or punch (2–4 dB)",
      "Parallel crush for weight if needed",
      "Bass track leveled so every note lands evenly",
      "Sidechain where kick and bass collide",
      "Match output level before judging"
    ],
    "setup": false,
    "links": [
      [
        "comp",
        "Compression table"
      ]
    ]
  },
  {
    "id": "w11",
    "t": "Vocals",
    "why": "In hip-hop the vocal is the lead instrument. Clarity and consistent level beat heavy effects.",
    "todo": [
      "Clean up: breaths, clicks, clip-gain uneven words",
      "Tuning if the style uses it",
      "Subtractive EQ → FET → de-esser → additive EQ → opto",
      "Doubles and adlibs placed and filtered",
      "Ride the volume word by word"
    ],
    "setup": false,
    "links": [
      [
        "vox",
        "Vocal chain"
      ]
    ]
  },
  {
    "id": "w12",
    "t": "Space & effects",
    "why": "Reverb and delay give depth, but in hip-hop dry and close usually wins. Time them to the tempo and keep them out of the low end.",
    "todo": [
      "Set delays to tempo",
      "Reverb pre-delay so words stay upfront",
      "High-pass returns at 200–400 Hz",
      "Duck returns with the vocal",
      "Automate throws on bar ends"
    ],
    "setup": false,
    "links": [
      [
        "fx",
        "Tempo tools"
      ]
    ]
  },
  {
    "id": "w13",
    "t": "Mix bus & references",
    "why": "Your room and ears adapt. A level-matched reference track is the only reliable judge.",
    "todo": [
      "Light glue on the mix bus (2:1, 1–2 dB)",
      "Import a reference in the same style, level-matched",
      "Check phone speaker, earbuds, car, mono",
      "Take a break, then listen once from start to finish",
      "Fix the biggest problem first, not the smallest"
    ],
    "setup": false,
    "links": [
      [
        "fix",
        "Fixes"
      ]
    ]
  },
  {
    "id": "w14",
    "t": "Master & export",
    "why": "Mastering adds final polish and loudness. Hit the style's loudness range, keep a safe true peak, and deliver the files people will ask for.",
    "todo": [
      "Master chain: EQ → glue → clip → limiter, ceiling −1 dBTP",
      "Hit the loudness target for the style",
      "Bounce 24-bit WAV (or 32-bit float) at session rate",
      "Unmastered mix bounce for a mastering engineer",
      "Instrumental, acapella, and TV track (beat + hooks)",
      "Stems: drums, bass, sample, vocals, FX",
      "Name files Title_BPM_Key_v1"
    ],
    "setup": false,
    "links": [
      [
        "master",
        "Master tab"
      ]
    ]
  }
]
